"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ShieldCheck, ArrowRight, FileText, CircleCheck, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  standardBadge: string;
  title: string;
  lead: string;
  secondaryCta: string;
  cardChip: string;
  specNote: string;
  specLinkText: string;
  specs: {
    label: string;
    val: string;
  }[];
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Filtration Engineering & Mechanics",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Filtration Fundamentals",
    lead: "A comprehensive technical foundation on particle retention mechanisms, micron ratings, wire weave architectures, and hydraulic mechanics for industrial filtration media. Compare surface versus depth filtration principles and understand nominal versus absolute ratings validated to ASTM E128 and ISO 4003 bubble point standards.",
    secondaryCta: "Mesh Count & Aperture Guide",
    cardChip: "Precision Woven & Sintered Media",
    specNote: "Single-layer woven mesh and multilayer sintered laminates",
    specLinkText: "View Datasheets",
    specs: [{
      label: "Micron Range",
      val: "1 µm – 25 mm"
    }, {
      label: "Retention Mechanism",
      val: "Surface & Depth"
    }, {
      label: "Rating Standard",
      val: "ASTM E128 / ISO 4003"
    }, {
      label: "Common Alloys",
      val: "AISI 304 / 316 / 316L"
    }]
  },
  zh: {
    categoryBadge: "过滤工程与流体力学",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "过滤基础",
    lead: "工业过滤介质颗粒拦截机理、微米级过滤精度、编织丝网结构及流体力学的完整工程基础。深入解析表面过滤与深层过滤的核心差异，以及符合 ASTM E128 和 ISO 4003 泡点法标准的公称与绝对精度标定规范。",
    secondaryCta: "目数与孔径规格指南",
    cardChip: "精密编织与烧结过滤介质",
    specNote: "精密单层金属编织网与多层扩散烧结金属网复合板",
    specLinkText: "查阅技术数据表",
    specs: [{
      label: "微米精度范围",
      val: "1 µm – 25 mm"
    }, {
      label: "拦截机理",
      val: "表面过滤 / 深层过滤"
    }, {
      label: "测试验证标准",
      val: "ASTM E128 / ISO 4003"
    }, {
      label: "常用材质牌号",
      val: "AISI 304 / 316 / 316L"
    }]
  },
  es: {
    categoryBadge: "Ingeniería y mecánica de filtración",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Fundamentos de filtración",
    lead: "Base técnica completa sobre mecanismos de retención de partículas, clasificaciones en micras, estructuras de tejido de alambre y mecánica hidráulica para medios filtrantes industriales. Compare los principios de filtración en superficie frente a profundidad y comprenda las clasificaciones nominales frente a absolutas según las normas ASTM E128 e ISO 4003.",
    secondaryCta: "Guía de número de malla y abertura",
    cardChip: "Medios tejidos y sinterizados de precisión",
    specNote: "Malla tejida de una sola capa y laminados sinterizados multicapa",
    specLinkText: "Ver fichas técnicas",
    specs: [{
      label: "Rango en micras",
      val: "1 µm – 25 mm"
    }, {
      label: "Mecanismo de retención",
      val: "Superficie y profundidad"
    }, {
      label: "Norma de evaluación",
      val: "ASTM E128 / ISO 4003"
    }, {
      label: "Aleaciones habituales",
      val: "AISI 304 / 316 / 316L"
    }]
  },
  de: {
    categoryBadge: "Filtrationstechnik & Mechanik",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Filtrationsgrundlagen",
    lead: "Fundierte technische Grundlagen zu Partikelrückhaltemechanismen, Mikron-Abscheidegraden, Drahtgewebestrukturen und Strömungsmechanik für industrielle Filtermedien. Vergleichen Sie Oberflächen- und Tiefenfiltration und verstehen Sie nominale sowie absolute Abscheidegrade nach den Normen ASTM E128 und ISO 4003.",
    secondaryCta: "Leitfaden für Maschenzahl & Maschenweite",
    cardChip: "Präzisionsgewebe & Sintermedien",
    specNote: "Präzises einlagiges Drahtgewebe und mehrlagige Sinterlaminate",
    specLinkText: "Datenblätter anzeigen",
    specs: [{
      label: "Mikron-Bereich",
      val: "1 µm – 25 mm"
    }, {
      label: "Rückhaltemechanismus",
      val: "Oberfläche & Tiefe"
    }, {
      label: "Prüfnorm",
      val: "ASTM E128 / ISO 4003"
    }, {
      label: "Gängige Legierungen",
      val: "AISI 304 / 316 / 316L"
    }]
  },
  fr: {
    categoryBadge: "Ingénierie & mécanique de filtration",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Fondamentaux de la filtration",
    lead: "Une base technique complète sur les mécanismes de rétention des particules, les seuils de coupure en microns, les structures de tissage métallique et l'hydraulique des médias filtrants industriels. Comparez les principes de filtration surfacique et en profondeur et maîtrisez les seuils nominaux et absolus validés selon les normes ASTM E128 et ISO 4003.",
    secondaryCta: "Guide du nombre de mailles & ouvertures",
    cardChip: "Médias tissés & frittés de précision",
    specNote: "Toile tissée monocouche de précision et laminés frittés multicouches",
    specLinkText: "Consulter les fiches techniques",
    specs: [{
      label: "Plage en microns",
      val: "1 µm – 25 mm"
    }, {
      label: "Mécanisme de rétention",
      val: "Surface & Profondeur"
    }, {
      label: "Norme d'essai",
      val: "ASTM E128 / ISO 4003"
    }, {
      label: "Alliages courants",
      val: "AISI 304 / 316 / 316L"
    }]
  },
  pt: {
    categoryBadge: "Engenharia e mecânica de filtração",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Fundamentos de filtração",
    lead: "Fundamentação técnica completa sobre mecanismos de retenção de partículas, graus de filtração em mícrons, arquiteturas de tecelagem metálica e hidráulica de meios filtrantes industriais. Compare os princípios de filtração superficial e em profundidade e compreenda as classificações nominais versus absolutas validadas pelas normas ASTM E128 e ISO 4003.",
    secondaryCta: "Guía de contagem de malha e abertura",
    cardChip: "Meios tecidos e sinterizados de precisão",
    specNote: "Telas tecidas monocamada de precisão e laminados sinterizados multicamadas",
    specLinkText: "Ver fichas técnicas",
    specs: [{
      label: "Faixa em mícrons",
      val: "1 µm – 25 mm"
    }, {
      label: "Mecanismo de retenção",
      val: "Superfície e profundidade"
    }, {
      label: "Norma de avaliação",
      val: "ASTM E128 / ISO 4003"
    }, {
      label: "Ligas comuns",
      val: "AISI 304 / 316 / 316L"
    }]
  }
};
export default function FiltrationHero() {
  const {
    lang
  } = useLanguage();
  const t = pick(lang, copyData);
  return <section id="filtrationfundamentalshero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Exactly Two Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="rounded-md border-border bg-card px-3 py-1 font-mono mf-caption">
                <SlidersHorizontal className="size-3.5 text-primary" />
                {t.categoryBadge}
              </Badge>
              <Badge variant="outline" className="rounded-md border-border bg-muted px-3 py-1 font-mono text-foreground mf-caption">
                <ShieldCheck className="size-3.5 text-primary" />
                {t.standardBadge}
              </Badge>
            </div>

            {/* Main Headline - Single H1 for the page */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {t.title}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {t.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/MeshCountAndAperturePage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" />
                  <span>{t.secondaryCta}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Hero Display Card (5 Columns on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="mf-card-media mf-ratio-hero relative w-full overflow-hidden">
                <img src="https://mutualfilter.com/wp-content/uploads/2026/09/plain-stainless-wire-mesh.webp" alt="Industrial precision stainless steel wire mesh weave structure for filtration media" loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" />
                  <span>{t.cardChip}</span>
                </div>
              </div>

              {/* Single Footer Row */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" />
                    <span>{t.specNote}</span>
                  </div>
                  <Link to="/DatasheetsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{t.specLinkText}</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid (4 items) */}
        <div className="mf-spec-strip">
          {t.specs.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}